import { formatTimestampValueForExport } from '@utils/timezoneDisplay'

/** RFC 4180-style parse: quoted fields may contain commas and newlines. */
export function parseMetricsExportCsv(input: string): string[][] {
  const result: string[][] = []
  let row: string[] = []
  let field = ''
  let inQuotes = false

  const flushField = () => {
    row.push(field)
    field = ''
  }

  for (let i = 0; i < input.length; i++) {
    const ch = input[i]!
    if (inQuotes) {
      if (ch === '"') {
        if (input[i + 1] === '"') {
          field += '"'
          i++
        } else {
          inQuotes = false
        }
      } else {
        field += ch
      }
    } else if (ch === '"') {
      inQuotes = true
    } else if (ch === ',') {
      flushField()
    } else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && input[i + 1] === '\n') i++
      flushField()
      result.push(row)
      row = []
    } else {
      field += ch
    }
  }
  flushField()
  result.push(row)

  while (
    result.length > 1 &&
    result[result.length - 1]?.length === 1 &&
    result[result.length - 1]![0] === ''
  ) {
    result.pop()
  }

  return result
}

function escapeCsvField(s: string): string {
  if (/[",\r\n]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`
  }
  return s
}

export function serializeMetricsExportCsv(rows: string[][]): string {
  return rows.map((r) => r.map(escapeCsvField).join(',')).join('\n')
}

function isCreatedAtColumnHeader(h: string): boolean {
  return h === 'created_at' || h.endsWith('.created_at')
}

/**
 * Backend CSV export writes `created_at` as raw epoch microseconds (digits only).
 * `formatTimestampValueForExport` expects numbers for microsecond epochs; coerce digit strings.
 */
function formatCreatedAtCell(raw: string): string {
  const t = raw.trim()
  if (t === '') return raw
  if (/^\d+$/.test(t)) {
    const n = Number(t)
    if (Number.isFinite(n)) {
      return formatTimestampValueForExport(n)
    }
  }
  return formatTimestampValueForExport(t)
}

/** Post-process workflow / project / tenant metrics CSV text before download. */
export function formatCreatedAtInMetricsExportCsv(csvText: string): string {
  const rows = parseMetricsExportCsv(csvText)
  if (rows.length === 0) return csvText

  const header = rows[0]!
  const indices = header
    .map((h, i) => (isCreatedAtColumnHeader(h.trim()) ? i : -1))
    .filter((i) => i >= 0)
  if (indices.length === 0) return csvText

  for (let r = 1; r < rows.length; r++) {
    const line = rows[r]!
    for (const idx of indices) {
      if (idx < line.length) {
        line[idx] = formatCreatedAtCell(line[idx] ?? '')
      }
    }
  }

  return serializeMetricsExportCsv(rows)
}
